import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash';
import { connect } from 'react-redux';
import Markdown from 'markdown-to-jsx';
import Svg from 'svg-inline-react';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { history } from '@src/redux';
import { setFlashMessage } from '@src/redux/actions/flashMessages';
import { toggleNotificationCenterVisibility } from '@src/redux/actions/notificationCenter';
import { Avatar } from '@src/components/Avatar';
import { ListPopover } from '@src/components/Popover';
import { OutsideClick } from '@src/components/OutsideClick';
import FrDotsSvg from '@src/svgs/fr-more.svg';
import { BookmarkTypeEnum, useMarkNotificationAsReadMutation, useRemoveBookmarkMutation } from '@src/graphql/generated';
import { trackEvent } from '@src/metrics';
import './styles.scss';

const Notification = ({ notification, setFlashMessage, toggleNotificationCenterVisibility }) => {
  const getBookmarkInfo = () => {
    if (get(notification, 'opening.id') || get(notification, 'application.opening.id')) {
      return {
        id: get(notification, 'opening.id') || get(notification, 'application.opening.id'),
        label: 'opening',
        type: BookmarkTypeEnum.OPENING,
      };
    }

    return undefined;
  };

  const bookmarkedEntity = getBookmarkInfo();

  const [isMenuVisible, setIsMenuVisible] = useState(false);

  const [markAsReadMutate] = useMarkNotificationAsReadMutation();
  const markAsRead = notificationId => markAsReadMutate({ variables: { notificationId } });

  const openUrl = () => {
    markAsRead(notification.id);

    if (notification.url) {
      history.push(notification.url);
      trackEvent({
        context: MetricContext.NOTIFICATION_CENTER,
        interaction: MetricAction.OPEN_NOTIFICATION,
        payload: {
          'Notification Entity ID': get(bookmarkedEntity, 'id'),
          'Notification Entity Type': get(bookmarkedEntity, 'type'),
          'Notification ID': notification.id,
          'Notification Type': notification.type,
          'Notification Url': notification.url,
        },
      });

      toggleNotificationCenterVisibility();
    }

    trackEvent({
      context: MetricContext.NOTIFICATION_CENTER,
      interaction: MetricAction.MARK_NOTIFICATION_AS_READ,
      payload: {
        'Notification Entity ID': get(bookmarkedEntity, 'id'),
        'Notification Entity Type': get(bookmarkedEntity, 'type'),
        'Notification ID': notification.id,
        'Notification Type': notification.type,
        'Notification Url': notification.url,
      },
    });
  };

  const [removeBookmarkMutation] = useRemoveBookmarkMutation();
  const removeBookmark = () => {
    const bookmarkedEntity = getBookmarkInfo();

    if (!bookmarkedEntity) {
      return;
    }

    removeBookmarkMutation({
      variables: {
        entityId: bookmarkedEntity.id,
        type: bookmarkedEntity.type,
      },
    });

    trackEvent({
      context: MetricContext.NOTIFICATION_CENTER,
      interaction: MetricAction.UNFOLLOW_NOTIFICATION_ENTITY,
      payload: {
        'Notification Entity ID': bookmarkedEntity.id,
        'Notification Entity Type': bookmarkedEntity.type,
        'Notification ID': notification.id,
        'Notification Type': notification.type,
        'Notification Url': notification.url,
      },
    });

    setFlashMessage({
      title: `You won't be notified about this ${bookmarkedEntity.label}.`,
      type: 'success',
    });
  };

  return (
    <div className="notification" onMouseLeave={() => setIsMenuVisible(false)}>
      <div className="notification__avatar-container">
        <Avatar size="medium" source={notification.icons.primary.fullUrl} />
        {!!notification.icons.secondary && (
          <Avatar className="notification__avatar-small" size="x-small" source={notification.icons.secondary.fullUrl} />
        )}
      </div>
      <div
        className={classnames(
          'notification__content-container',
          !!notification.url && 'notification__content-container--has-link',
        )}
        onClick={() => openUrl()}
      >
        <p className="notification__message">
          <Markdown>{notification.message}</Markdown>
        </p>
      </div>
      {bookmarkedEntity && (
        <OutsideClick
          addOutsideClickListener={isMenuVisible}
          onOutsideClick={e => {
            e.preventDefault();
            setIsMenuVisible(false);
          }}
        >
          <div className="notification__menu-container">
            <span onClick={() => setIsMenuVisible(!isMenuVisible)}>
              <Svg className="notification__icon" src={FrDotsSvg} />
            </span>

            <ListPopover
              className="notification__menu-container__popover"
              list={[{ label: `Don't notify me about this ${bookmarkedEntity.label}` }]}
              onItemClick={() => {
                removeBookmark();
                setIsMenuVisible(false);
              }}
              show={isMenuVisible}
            />
          </div>
        </OutsideClick>
      )}
    </div>
  );
};

Notification.propTypes = {
  notification: PropTypes.shape({
    application: PropTypes.shape({
      opening: PropTypes.shape({
        recruiter: PropTypes.shape({
          id: PropTypes.string,
          nameInitials: PropTypes.string,
        }),
      }),
    }),
    icons: PropTypes.shape({
      primary: PropTypes.shape({
        fullUrl: PropTypes.string,
        id: PropTypes.string,
      }).isRequired,
      secondary: PropTypes.shape({
        fullUrl: PropTypes.string,
        id: PropTypes.string,
      }),
    }),
    id: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    opening: PropTypes.shape({
      recruiter: PropTypes.shape({
        id: PropTypes.string,
        nameInitials: PropTypes.string,
      }),
    }),
    readAt: PropTypes.string,
    sentAt: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    url: PropTypes.string,
  }).isRequired,
  setFlashMessage: PropTypes.func.isRequired,
  toggleNotificationCenterVisibility: PropTypes.func.isRequired,
};

export default connect(state => state, {
  setFlashMessage,
  toggleNotificationCenterVisibility,
})(Notification);
